import React, { ChangeEvent, Dispatch, FC, useCallback } from 'react';
import { FormGroup, Input, Label } from 'reactstrap';
import { DealerModel, FormNodeDealerPIIModel } from '../../../../shared/dealer';
import { CountrySelector, InlineHelp, RegionSelector } from '../../../components';
import { FormActionType } from '../../form/FormReducer';

interface DealerPiiProps {
  readonly node: FormNodeDealerPIIModel;
  readonly state: DealerModel;
  readonly dispatch: Dispatch<FormActionType>;
}

export const DealerPii: FC<DealerPiiProps> = ({ node, state, dispatch }) => {
  const width = node.width && node.width === 'half' ? 6 : 12;
  return (
    <FormGroup className={`col-${width}`}>
      <Label for={node.id}>
        {node.displayName}
        {node.infoText && <InlineHelp>{node.infoText}</InlineHelp>}
      </Label>
      <PiiInput dispatch={dispatch} node={node} state={state} />
    </FormGroup>
  );
};

const PiiInput: FC<DealerPiiProps> = ({ node, state, dispatch }) => {
  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: 'updateDealerPii', subType: node.subType, value: e.target.value || null });
    },
    [dispatch, node],
  );

  const onStringChange = useCallback(
    (value: string) => {
      dispatch({ type: 'updateDealerPii', subType: node.subType, value: value || null });
    },
    [dispatch, node],
  );

  switch (node.subType) {
    case 'addressCountry': {
      return (
        <CountrySelector
          autoComplete="country-name"
          className="form-control"
          id={node.id}
          name="addressCountry"
          onChange={onStringChange}
          value={state.addressCountry}
          valueType="short"
        />
      );
    }

    case 'addressState': {
      return (
        <RegionSelector
          autoComplete="address-level1"
          className="form-control"
          country={state.addressCountry}
          countryValueType="short"
          data-coerce={false}
          disableWhenEmpty
          id={node.id}
          name="addressState"
          onChange={onStringChange}
          value={state.addressState}
          valueType="short"
        />
      );
    }

    default: {
      return (
        <Input
          id={node.id}
          name={node.subType}
          onChange={onInputChange}
          type={node.subType === 'email' ? 'email' : undefined}
          value={state[node.subType] ?? undefined}
        />
      );
    }
  }
};
